/**
 * Dependency relationship rule (based on category)
 */
import { RuleType, BILLING_CYCLE, BillingCycleMap } from '../constants';
import { PlanRule } from './PlanRule';

export class DependencyRule extends PlanRule {
  constructor(dependentCategory, requiredCategories, options = {}) {
    let description = `Category ${dependentCategory} depends on one of: ${requiredCategories.join(', ')}`;
    if (options.licenseEqual && typeof options.licenseEqual === 'function') {
      description += ` (with custom license equality logic)`;
    }
    if (options.source) {
      const { plan, className } = options.source;
      description += ` (Source: ${plan}${className ? ` -> ${className}` : ''})`;
    }
    super(RuleType.DEPENDENCY, description);
    this.dependentCategory = dependentCategory;
    this.requiredCategories = requiredCategories; // Array, indicates dependency on one of these categories
    this.billingCycleMatch = options.billingCycleMatch || false; // Whether billing cycle needs to match (less than or equal)
    this.billingCycleEqual = options.billingCycleEqual || false; // Whether billing cycle needs to be exactly equal
    this.licenseMatch = options.licenseMatch ?? true; // Whether billing cycle needs to match
    this.licenseEqual = options.licenseEqual || false; // Whether license needs to be exactly equal
    this.source = options.source;
  }

  validate(cart, planToAdd) {
    if (planToAdd.category !== this.dependentCategory) {
      return { valid: true };
    }

    const hasRequiredCategory = this.requiredCategories.some((requiredCategory) => cart.hasCategory(requiredCategory));

    if (!hasRequiredCategory) {
      return {
        valid: false,
        error: `Cannot add ${planToAdd.planCode}: requires one of categories ${this.requiredCategories.join(
          ', ',
        )} to be added first`,
      };
    }

    // Check billing cycle consistency
    if (this.billingCycleEqual) {
      // Check if billing cycle is exactly equal
      const requiredPlans = cart.getPlansByCategories(
        this.requiredCategories.find((category) => cart.hasCategory(category)),
      );
      const hasMatchingCycle = requiredPlans.some(
        (requiredPlan) =>
          requiredPlan.billingCycle === planToAdd.billingCycle ||
          requiredPlan.billingCycle === BILLING_CYCLE.PAY_AS_YOU_GO,
      );
      if (!hasMatchingCycle) {
        const requiredPlan = requiredPlans[0];
        return {
          valid: false,
          error: `Cannot add ${planToAdd.planCode}: billing cycle must be exactly equal to ${requiredPlan.planCode} (${requiredPlan.billingCycle})`,
        };
      }
    } else if (this.billingCycleMatch) {
      const requiredPlans = cart.getPlansByCategories(
        this.requiredCategories.find((category) => cart.hasCategory(category)),
      );
      // Find the one with the largest billing cycle in dependencyPlans
      const requiredPlan = requiredPlans.reduce((max, current) => {
        return BillingCycleMap[current.billingCycle] < BillingCycleMap[max.billingCycle] ? max : current;
      }, {});
      if (requiredPlan && BillingCycleMap[requiredPlan.billingCycle] < BillingCycleMap[planToAdd.billingCycle]) {
        return {
          valid: false,
          error: `Cannot add ${planToAdd.planCode}: billing cycle must match ${requiredPlan.planCode} (${requiredPlan.billingCycle})`,
        };
      }
    }

    return { valid: true };
  }
}
